import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import { Check } from "lucide-react";
import { VENDOR_TOGGLEABLE_COLUMNS } from "./vendorTableColumns";

const VendorColumnsDropdown = ({ visibleColumns, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const containerRef = useRef(null);
    const menuRef = useRef(null);

    const updateMenuPosition = () => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        setMenuStyle({
            position: "fixed",
            left: rect.left,
            top: rect.bottom + 4,
            minWidth: Math.max(rect.width, 160),
            zIndex: 100,
        });
    };

    useLayoutEffect(() => {
        if (!isOpen) return;
        updateMenuPosition();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (
                containerRef.current?.contains(event.target) ||
                menuRef.current?.contains(event.target)
            ) {
                return;
            }
            setIsOpen(false);
        };

        const handleViewportChange = () => updateMenuPosition();

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("resize", handleViewportChange);
        window.addEventListener("scroll", handleViewportChange, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("resize", handleViewportChange);
            window.removeEventListener("scroll", handleViewportChange, true);
        };
    }, [isOpen]);

    const toggleColumn = (columnId) => {
        onChange({
            ...visibleColumns,
            [columnId]: !visibleColumns[columnId],
        });
    };

    const menu = isOpen ? (
        <div
            ref={menuRef}
            role="menu"
            aria-orientation="vertical"
            className="overflow-hidden rounded-md border border-border bg-white p-1 shadow-md"
            style={menuStyle || { position: "fixed", left: -9999, top: -9999, visibility: "hidden" }}
        >
            {VENDOR_TOGGLEABLE_COLUMNS.map((column) => {
                const checked = visibleColumns[column.id] !== false;

                return (
                    <button
                        key={column.id}
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={checked}
                        onClick={() => toggleColumn(column.id)}
                        className="relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-[15px] outline-none select-none hover:bg-gray-100"
                    >
                        <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
                            {checked && <Check className="size-3.5" />}
                        </span>
                        {column.label}
                    </button>
                );
            })}
        </div>
    ) : null;

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                className="inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-md border border-border bg-white px-3 text-sm shadow-sm outline-none transition-colors hover:bg-gray-50"
            >
                <AdjustmentsHorizontalIcon className="w-4 h-4 text-gray-500" />
                Columns
            </button>

            {menu && createPortal(menu, document.body)}
        </div>
    );
};

export default VendorColumnsDropdown;
